import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import type { ABTest } from "@/types/ab-tests";
import { ProductTableCell as _ProductTableCell } from "@/components/shared/ProductTableCell";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { ViewTestModal } from "./ViewTestModal";
import { EditTestModal, type HeroAbTestEditFormPayload } from "./EditTestModal";
import { EditResultsDrawer } from "./EditResultsDrawer";
import { UploadResultsModal } from "./UploadResultsModal";
import { toast } from "@/hooks/use-toast";
import type { HeroAbTestUpdatePayload } from "@/resources/product/product.api";
import { ABTestTableHeader } from "./ABTestTable/ABTestTableHeader";
import { ABTestTableRow } from "./ABTestTable/ABTestTableRow";
import { ScrollArrows } from "./ABTestTable/ScrollArrows";

export interface ABTestWithProduct extends ABTest {
  heroProductId?: number;
  heroProductName?: string;
  heroProductImage?: string;
  heroProductTitle?: string;
  heroBulletPoints?: string[];
  heroAsin?: string;
}

interface ABTestTableProps {
  tests: ABTestWithProduct[];
  currentTitle: string;
  bulletPoints: string[];
  productId?: number;
  brandId?: number | null;
  onUpdateTest?: (args: { productId: number; testId: string; body: HeroAbTestUpdatePayload }) => Promise<void>;
  isUpdateTestPending?: boolean;
  onDeleteTest?: (testId: string) => void;
  onDeleteTests?: (testIds: string[]) => void;
  onUploadResults?: (testId: string, file: File, outcome?: string, variantBSwitchDate?: string) => Promise<void>;
  onResumeDraft?: (test: ABTest) => void;
  showProductColumn?: boolean;
  readOnly?: boolean;
}

export function ABTestTable({
  tests,
  currentTitle,
  bulletPoints,
  productId: productIdProp,
  brandId,
  onUpdateTest,
  isUpdateTestPending = false,
  onDeleteTest,
  onDeleteTests: _onDeleteTests,
  onUploadResults,
  onResumeDraft,
  showProductColumn,
  readOnly = false,
}: ABTestTableProps) {
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editResultsOpen, setEditResultsOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<ABTest | null>(null);
  const [uploadTest, setUploadTest] = useState<ABTestWithProduct | null>(null);
  const [testToDelete, setTestToDelete] = useState<ABTest | null>(null);

  useEffect(() => {
    if (selectedTest) {
      const updated = tests.find((t) => t.id === selectedTest.id);
      if (updated) setSelectedTest(updated);
    }
  }, [tests]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(overflow > 4 && el.scrollLeft < overflow - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScroll();
    el.addEventListener("scroll", updateScroll, { passive: true });
    const ro = new ResizeObserver(updateScroll);
    ro.observe(el);
    const inner = el.firstElementChild;
    if (inner) ro.observe(inner);
    return () => {
      el.removeEventListener("scroll", updateScroll);
      ro.disconnect();
    };
  }, [updateScroll, tests.length]);

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: "smooth" });
  };

  const handleSaveTest = async (updatedTest: HeroAbTestEditFormPayload) => {
    if (!onUpdateTest) {
      toast({ title: "Test Updated", description: `Test "${updatedTest.intentFrom} \u2192 ${updatedTest.intentTo}" has been updated.` });
      return;
    }
    if (brandId == null) {
      toast({ title: "Brand required", description: "Select a brand before saving.", variant: "destructive" });
      throw new Error("Brand required");
    }
    const pid = productIdProp ?? tests.find((t) => t.id === updatedTest.id)?.heroProductId;
    if (pid == null) {
      toast({ title: "Cannot save", description: "Missing product context for this test.", variant: "destructive" });
      throw new Error("Missing product id");
    }
    const body: HeroAbTestUpdatePayload = {
      scope_fields: updatedTest.scopeFields,
      content_type: updatedTest.contentType,
      intent_from: updatedTest.intentFrom,
      intent_to: updatedTest.intentTo,
      hypothesis: updatedTest.hypothesis,
      target_phrase: updatedTest.targetPhrase.trim() || undefined,
      variant_a: { title: updatedTest.variantATitle, bullets: updatedTest.variantABullets },
      variant_b: { title: updatedTest.variantBTitle, bullets: updatedTest.variantBBullets },
    };
    await onUpdateTest({ productId: pid, testId: updatedTest.id, body });
    toast({ title: "Test updated", description: `Test "${updatedTest.intentFrom} \u2192 ${updatedTest.intentTo}" has been saved.` });
  };

  if (tests.length === 0) {
    return (
      <div className="border-t border-border text-center py-10 text-muted-foreground text-xs">
        No tests configured yet. Create a new test to get started.
      </div>
    );
  }

  return (
    <>
      <div className="relative w-full">
        <div ref={scrollRef} data-scrolled={canScrollLeft ? "true" : "false"} className="overflow-x-scroll table-scroll w-full min-w-0 border-t border-border">
          <div className="min-w-max">
            <ABTestTableHeader showProductColumn={!!showProductColumn} readOnly={readOnly} />
            {tests.map((test) => (
              <ABTestTableRow
                key={test.id}
                test={test}
                showProductColumn={!!showProductColumn}
                readOnly={readOnly}
                productIdProp={productIdProp}
                onUpdateTest={onUpdateTest}
                onResumeDraft={onResumeDraft}
                onView={(t) => { setSelectedTest(t); setViewModalOpen(true); }}
                onEdit={(t) => { setSelectedTest(t); setEditModalOpen(true); }}
                onEditResults={(t) => { setSelectedTest(t); setEditResultsOpen(true); }}
                onUploadResults={(t) => { setUploadTest(t); setUploadModalOpen(true); }}
                onDelete={(t) => setTestToDelete(t)}
              />
            ))}
          </div>
        </div>
        <ScrollArrows
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          onScrollLeft={() => scrollBy(-1)}
          onScrollRight={() => scrollBy(1)}
        />
      </div>

      {selectedTest && (
        <ViewTestModal
          open={viewModalOpen}
          onOpenChange={setViewModalOpen}
          test={selectedTest}
          productId={(selectedTest as ABTestWithProduct).heroProductId ?? productIdProp}
          brandId={brandId}
          onEdit={readOnly ? undefined : (t) => { setSelectedTest(t); setEditModalOpen(true); }}
          onUploadResults={onUploadResults}
          onEditConfiguration={readOnly || !onResumeDraft ? undefined : (t) => { setViewModalOpen(false); onResumeDraft(t); }}
          onSavePatch={onUpdateTest ? (testId, patch) => {
            const pid = (selectedTest as ABTestWithProduct).heroProductId ?? productIdProp;
            if (!pid) return;
            void onUpdateTest({ productId: pid, testId, body: patch as HeroAbTestUpdatePayload });
          } : undefined}
        />
      )}
      {selectedTest && (
        <EditResultsDrawer
          open={editResultsOpen}
          onOpenChange={setEditResultsOpen}
          test={selectedTest}
          productId={(selectedTest as ABTestWithProduct).heroProductId ?? productIdProp ?? null}
          brandId={brandId ?? null}
          onReuploadResults={onUploadResults ? () => { setUploadTest(selectedTest as ABTestWithProduct); setUploadModalOpen(true); } : undefined}
          onSavePatch={onUpdateTest ? (testId, patch) => {
            const pid = (selectedTest as ABTestWithProduct).heroProductId ?? productIdProp;
            if (!pid) return;
            void onUpdateTest({ productId: pid, testId, body: patch as HeroAbTestUpdatePayload });
          } : undefined}
        />
      )}
      {selectedTest && (
        <EditTestModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          test={selectedTest}
          currentTitle={(selectedTest as ABTestWithProduct).heroProductTitle ?? currentTitle}
          bulletPoints={(selectedTest as ABTestWithProduct).heroBulletPoints ?? bulletPoints}
          productId={(selectedTest as ABTestWithProduct).heroProductId ?? productIdProp}
          brandId={brandId}
          onSave={handleSaveTest}
          isSaving={isUpdateTestPending}
        />
      )}
      {uploadTest && (
        <UploadResultsModal
          open={uploadModalOpen}
          onOpenChange={setUploadModalOpen}
          testId={uploadTest.id}
          testName={uploadTest.name || uploadTest.intentShift.to}
          onUpload={(id, file, outcome, variantBSwitchDate) => void onUploadResults?.(id, file, outcome, variantBSwitchDate)}
        />
      )}
      <ConfirmDeleteDialog
        open={!!testToDelete}
        onOpenChange={(open) => { if (!open) setTestToDelete(null); }}
        title="Delete this test?"
        description={`This will permanently remove the A/B test "${testToDelete?.intentShift.from} → ${testToDelete?.intentShift.to}". This action cannot be undone.`}
        onConfirm={() => {
          if (testToDelete) onDeleteTest?.(testToDelete.id);
          setTestToDelete(null);
        }}
      />
    </>
  );
}
